import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import pick from 'lodash/pick';
import moment from 'moment';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { getMeetingItemByUuid } from 'Shared/selectors/common';
import { weekDaysOptions } from 'Shared/constants/constants';
import { RecurringWorkspaceItem, ItemType } from 'Shared/constants/types';
import { deleteMeetingItemRecurrence, upsertMeetingItemRecurrence } from 'Shared/actions/todos';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpButton from 'Wpds/components/button/WpButton';
import RecurrenceEditor from './RecurrenceEditor';

import './RecurringItemModal.scss';

const neededItemFields = Object.freeze(['recurringWorkspaceItem', 'itemType']);

type Props = {
  onToggle(): void;
  itemUuid: string;
};

const RecurringItemModal = ({ onToggle, itemUuid }: Props) => {
  const dispatch = useDispatch();
  const { tr } = useTranslation();
  const selectorResult = useDeepEqualSelector((state: any) =>
    pick(getMeetingItemByUuid(itemUuid)(state), neededItemFields)
  );

  const { recurringWorkspaceItem, itemType } = (selectorResult as {
    recurringWorkspaceItem: RecurringWorkspaceItem;
    itemType: ItemType;
  }) || {
    recurringWorkspaceItem: null,
    itemType: ItemType.discussionTopic,
  };

  const dayTodayNumber = new Date().getDay();
  const dayToday = weekDaysOptions.find(({ dayNumber }) => dayNumber === dayTodayNumber).value;
  const initialDayValue = recurringWorkspaceItem?.dayOfWeek || dayToday;

  const [intervalAmount, setIntervalAmount] = useState(
    recurringWorkspaceItem ? recurringWorkspaceItem.intervalAmount : 1
  );
  const [intervalUnit, setIntervalUnit] = useState(
    recurringWorkspaceItem ? recurringWorkspaceItem.intervalUnit : 'week'
  );
  const [day, setDay] = useState(initialDayValue);
  const [startsAt, setStartsAt] = useState(
    recurringWorkspaceItem?.startsAt ? moment(recurringWorkspaceItem.startsAt) : moment()
  );
  const [timeOfDay, setTimeOfDay] = useState(recurringWorkspaceItem ? recurringWorkspaceItem.timeOfDay : '09:00');
  const [dayInMonth, setDayInMonth] = useState(recurringWorkspaceItem ? recurringWorkspaceItem.dayInMonth : null);
  const [weekInMonth, setWeekInMonth] = useState(recurringWorkspaceItem ? recurringWorkspaceItem.weekInMonth : null);
  const [saving, setSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleSetIntervalAmount = amount => {
    if (!amount) {
      setHasError(true);
    } else {
      setHasError(false);
    }
    setIntervalAmount(amount);
  };

  const saveRecurringItem = async () => {
    setSaving(true);
    await dispatch(
      upsertMeetingItemRecurrence({
        workspaceItemUuid: itemUuid,
        dayOfWeek: day,
        startsAt: moment(startsAt).format(),
        timeOfDay,
        intervalAmount,
        intervalUnit,
        itemType,
        dayInMonth,
        weekInMonth,
        isEdit: Boolean(recurringWorkspaceItem),
      })
    );
    setSaving(false);
    onToggle();
  };

  const deleteeRecurringItem = useCallback(async () => {
    setSaving(true);
    await dispatch(
      deleteMeetingItemRecurrence({
        workspaceItemUuid: itemUuid,
        itemType,
      })
    );
    setSaving(false);
    onToggle();
  }, [itemUuid, itemType, setSaving, dispatch, onToggle]);
  return (
    <WpModal size="sm" isOpen onToggle={onToggle}>
      <WpModalHeader>
        {tr(
          'perform.recurrence_modal.title_create_dynamic',
          itemType === ItemType.actionItem
            ? tr('perform.recurrence_modal.item_type_item')
            : tr('perform.recurrence_modal.item_type_topic')
        )}
      </WpModalHeader>
      <WpModalBody>
        <div className="RecurringItemModal">
          <p>{tr('perform.recurrence_modal.body_intro')}</p>
          <div className="RecurringItemModal__editor">
            <form>
              <RecurrenceEditor
                days={day}
                setDay={setDay}
                timeOfDay={timeOfDay}
                setTimeOfDay={setTimeOfDay}
                intervalUnit={intervalUnit}
                setIntervalUnit={setIntervalUnit}
                intervalAmount={intervalAmount}
                setIntervalAmount={handleSetIntervalAmount}
                startsAt={startsAt}
                setStartsAt={setStartsAt}
                dayInMonth={dayInMonth}
                setDayInMonth={setDayInMonth}
                weekInMonth={weekInMonth}
                setWeekInMonth={setWeekInMonth}
              />
            </form>
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        {recurringWorkspaceItem && (
          <div className="mr-auto">
            <WpButton disabled={saving} color="danger" onClick={deleteeRecurringItem}>
              {tr('perform.recurrence_modal.actions.remove')}
            </WpButton>
          </div>
        )}
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle} disabled={saving}>
            {tr('perform.common.cancel')}
          </WpButton>
          <WpButton loading={saving} disabled={hasError} onClick={saveRecurringItem}>
            {tr('perform.common.save')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default RecurringItemModal;
